import React from 'react';
import type { EmReadReceipt } from '../../email/types';
import { Icon } from '../Icon/Icon';

export interface EmailSeenByProps {
  receipts?: EmReadReceipt[];
  seenCount?: number;
  /** Collapsed message row — icon + count only. */
  compact?: boolean;
}

function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter((part) => part.length > 1)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function formatReadAt(iso: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} +${names.length - 2}`;
}

function latestReadAt(receipts: EmReadReceipt[]): string | undefined {
  let latest: string | undefined;
  let latestMs = -Infinity;
  for (const receipt of receipts) {
    const ms = Date.parse(receipt.readAt);
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latest = receipt.readAt;
    }
  }
  return latest;
}

export function EmailSeenBy({ receipts = [], seenCount, compact = false }: EmailSeenByProps) {
  const count = seenCount ?? receipts.length;
  if (!count) return null;

  const names = receipts.map((row) => row.name).filter(Boolean);
  const label = names.length ? formatNames(names) : `${count} staff`;
  const readAt = latestReadAt(receipts);
  const title = names.length
    ? receipts.map((row) => `${row.name}${row.readAt ? ` · ${formatReadAt(row.readAt)}` : ''}`).join('\n')
    : `Seen by ${count} staff`;

  if (compact) {
    return (
      <span className="em-seen-compact" title={title}>
        <Icon kind="eye" size={10} />
        Seen by {count}
      </span>
    );
  }

  const visible = receipts.slice(0, 3);

  return (
    <div className="em-seen-by" title={title}>
      <Icon kind="eye" size={12} style={{ color: 'var(--ink-4)', flex: 'none' }} />
      <span className="em-seen-label">Seen by</span>
      {visible.length ? (
        <span className="em-seen-avatars" aria-hidden="true">
          {visible.map((row) => (
            <span key={row.userId || row.name} className="em-seen-avatar">
              {initialsOf(row.name)}
            </span>
          ))}
        </span>
      ) : null}
      <span className="em-seen-names">{label}</span>
      {readAt ? <span className="em-seen-time">· {formatReadAt(readAt)}</span> : null}
    </div>
  );
}
